'use client'
import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {SectionAccordion} from '@fitro/customer-ui/SectionAccordion'
import {HistoryPagination} from '@fitro/customer-ui/HistoryPagination'
import {Button} from '@fitro/customer-ui/Button'
import {AcceptedPassList} from '../shared/AcceptedPassList'
import {resolvePartnerSlug} from '../../../lib/partner-slug'
import {useTranslation} from '../../../hooks/useTranslation'
import {getPublicAcceptedPasses} from '../../../services/public-accepted-passes.service'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'

/** A details-window row with the passes an Occurrence accepts; hidden when none is. */
export function PublicAcceptedPasses({event}: {event: PublicScheduleEvent}) {
    const [open, setOpen] = useState(false)
    const [page, setPage] = useState(0)
    const {t} = useTranslation('client')
    const {t: common} = useTranslation('common')
    const {t: landing} = useTranslation('landing')
    const slug = resolvePartnerSlug()
    const applicable = event.passAccessMode !== 'none' && event.entryMode?.toUpperCase() !== 'FREE'
        && !(event.passAccessMode === 'selected' && !event.allowedPassTemplateIds?.length)
    const query = useQuery({queryKey: ['public-accepted-passes', slug, event.id, page, 24],
        queryFn: ({signal}) => getPublicAcceptedPasses(slug!, event.id, page, signal),
        enabled: Boolean(slug) && applicable, staleTime: 60_000, retry: false})
    // The first page says whether any pass is accepted; until then nothing is shown.
    if (!applicable || (!query.data && !query.isError) || query.data?.page.totalElements === 0) return null
    const status = <>
        {query.isLoading ? <p role="status" className="text-sm text-gray-500">{common('actions.loading')}</p> : null}
        {query.isError ? <div role="alert"><p>{t('common.error')}</p><Button variant="outline" size="small" onClick={() => void query.refetch()}>{common('actions.retry')}</Button></div> : null}
    </>
    const list = query.data ? <>
        {query.data.content.length ? <AcceptedPassList passes={query.data.content}/> : <p className="text-sm text-gray-500">{t('detailsSections.noPasses')}</p>}
        <HistoryPagination page={page} totalPages={query.data.page.totalPages} onPageChange={setPage} loading={query.isFetching}
            previousLabel={common('pagination.previous')} nextLabel={common('pagination.next')}/>
    </> : null
    const total = query.data?.page.totalElements
    return <SectionAccordion variant="row" title={t('detailsSections.acceptedPasses')} isOpen={open} onToggle={() => setOpen(v => !v)}
        description={typeof total === 'number' ? landing('publicEntry.details.passCount', {count: total}) : null}>
        {status}
        {list}
    </SectionAccordion>
}
